'use client';

import React, { useState } from 'react';
import { CheckCircle2, Download, Printer, X, Package, Calendar, User, Cpu, Hash } from 'lucide-react';
import type { KitComponent } from '@/app/components/KittingWorkspace';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface KittingCompleteModalProps {
  components: KitComponent[];
  onClose: () => void;
}

const KIT_BARCODE = 'KIT-20260608-047|TRAUMA-B|PRASUN|08062026|09:31|SEAL-OK';

export default function KittingCompleteModal({ components, onClose }: KittingCompleteModalProps) {
  const [printing, setPrinting] = useState(false);

  const handlePrint = () => {
    setPrinting(true);
    // Backend integration point: POST /api/kits/{kitId}/print-label
    setTimeout(() => {
      setPrinting(false);
      toast.success('Kit label sent to printer', { description: 'HH-01 → Zebra ZT411' });
    }, 1800);
  };

  const handleDownload = () => {
    // Backend integration point: GET /api/kits/{kitId}/traceability-report
    toast.success('Traceability report downloaded', { description: 'KIT-20260608-047.pdf' });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kit-complete-title"
    >
      <div className="card-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
              <CheckCircle2 size={20} className="text-primary" />
            </div>
            <div>
              <h2 id="kit-complete-title" className="text-base font-bold text-foreground">
                Kit Assembly Complete
              </h2>
              <p className="text-xs text-muted-foreground">All components verified</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            aria-label="Close modal"
          >
            <X size={16} className="text-muted-foreground" />
          </button>
        </div>

        {/* Kit barcode */}
        <div className="px-6 py-4 bg-primary/5 border-b border-border">
          <p className="text-xs text-muted-foreground mb-1.5 uppercase tracking-wider font-semibold">
            Generated Kit Barcode
          </p>
          <div className="bg-muted rounded-lg p-3 flex items-center gap-3">
            <Package size={20} className="text-primary flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-mono-data font-bold text-primary break-all">
                {KIT_BARCODE}
              </p>
              <p className="text-2xs text-muted-foreground mt-0.5">
                Kit ID | Type | Operator | Date | Time | Seal
              </p>
            </div>
          </div>
        </div>

        {/* Kit metadata */}
        <div className="px-6 py-4 border-b border-border">
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Hash, label: 'Kit ID', value: 'KIT-20260608-047' },
              { icon: Package, label: 'Kit Type', value: 'Trauma Kit — Type B' },
              { icon: User, label: 'Operator', value: 'Prasun Sharma' },
              { icon: Cpu, label: 'Device', value: 'HH-01' },
              { icon: Calendar, label: 'Date', value: '08-Jun-2026' },
              { icon: Calendar, label: 'Time', value: '09:31:22 IST' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={`meta-${item.label}`} className="bg-muted/50 rounded-lg p-2.5">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Icon size={10} className="text-muted-foreground" />
                    <p className="text-2xs text-muted-foreground uppercase tracking-wider">{item.label}</p>
                  </div>
                  <p className="text-xs font-semibold text-foreground font-mono-data">{item.value}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Component summary */}
        <div className="px-6 py-4 border-b border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Component Traceability ({components.length} items)
          </p>
          <div className="space-y-2">
            {components.map((comp) => (
              <div
                key={`modal-${comp.id}`}
                className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0"
              >
                <CheckCircle2 size={13} className="text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-foreground truncate">{comp.itemName}</p>
                  <p className="text-2xs font-mono-data text-muted-foreground">
                    {comp.batchScanned ?? comp.batchSuggested} · Exp: {comp.expiryDate}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-bold text-primary tabular-nums">
                    {comp.scannedQty}/{comp.requiredQty}
                  </p>
                  <p className="text-2xs text-muted-foreground">{comp.uom}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 flex items-center gap-3">
          <button
            onClick={handlePrint}
            disabled={printing}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold transition-all duration-150 hover:brightness-110 active:scale-95 disabled:opacity-60"
          >
            {printing ? (
              <>
                <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Printing...
              </>
            ) : (
              <>
                <Printer size={15} />
                Print Kit Label
              </>
            )}
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-muted text-foreground text-sm font-semibold transition-all duration-150 hover:bg-muted/70 active:scale-95 border border-border"
          >
            <Download size={15} />
            Export
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-lg border border-border text-muted-foreground text-sm font-medium hover:text-foreground hover:bg-muted transition-all duration-150"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}